import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

interface Claim {
  id: string;
  claimNumber: string;
}

interface PaymentsTabProps {
  claim: Claim;
}

const PaymentsTab: React.FC<PaymentsTabProps> = ({ claim }) => {
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    priorPayments: true,
    settlementPayments: true,
  });

  // Proper insurance industry data structure
  const sampleData = {
    // Coverage Limits (Policy Limits)
    coverageLimits: {
      coverageA: 350000, // Main Dwelling
      coverageB: 35000,  // Other Property (typically 10% of A)
      coverageC: 175000, // Personal Items (typically 50% of A)
      coverageD: 70000,  // ALE/Additional Living Expenses (typically 20% of A)
    },
    
    // RCV Table (Replacement Cost Value) - Gross Settlement Calculated Version
    rcvBreakdown: {
      coverageA: 85000,  // Main Dwelling RCV
      coverageB: 12000,  // Other Property RCV
      coverageC: 25000,  // Personal Items RCV
      coverageD: 8500,   // ALE RCV
    },
    
    // ACV Table (Actual Cash Value) - RCV minus depreciation
    acvBreakdown: {
      coverageA: 72000,  // Main Dwelling ACV (RCV - depreciation)
      coverageB: 10200,  // Other Property ACV
      coverageC: 21250,  // Personal Items ACV
      coverageD: 8500,   // ALE ACV (no depreciation - ACV = RCV)
    },
    
    // Deductibles
    deductibles: {
      windHail: 2500,    // Wind/Hail deductible
      allOtherPerils: 1000, // AOP deductible
      applied: 2500,     // Actually applied deductible
    },
    
    // Prior Payments (standard don't go to PA at all)
    priorPayments: [
      { id: '1', date: '2024-01-15', payer: 'Insurance Company', type: 'ACV Payment', amount: 15000, coverage: 'A' },
      { id: '2', date: '2024-02-10', payer: 'Insurance Company', type: 'Emergency Services', amount: 3500, coverage: 'A' },
      { id: '3', date: '2024-02-20', payer: 'Insurance Company', type: 'ALE Payment', amount: 2200, coverage: 'D' },
    ],
    
    // Undisputed Payments (insurance pays without binding final settlement)
    undisputedPayments: [
      { id: '1', date: '2024-03-01', amount: 8000, coverage: 'C', description: 'Personal Property - Undisputed' },
    ],
    
    // Settlement Payments (negotiated through litigation/mediation/arbitration/appraisal)
    settlementPayments: [
      { id: '1', date: '2024-03-15', type: 'Appraisal Award', amount: 45000, method: 'Wire', status: 'Completed', coverage: 'A' },
      { id: '2', date: '2024-03-20', type: 'Mediation Settlement', amount: 12000, method: 'Check', status: 'Completed', coverage: 'B' },
    ],
    
    // Post-Legal Settlement Payments (additional settlements after legal referral)
    postLegalSettlements: [
      { id: '1', date: '2024-04-10', type: 'Legal Settlement', amount: 25000, method: 'Wire', status: 'Completed', coverage: 'A', legalFees: 8333 },
    ],
    
    // Client Expenses (deducted from settlement)
    clientExpenses: [
      { id: '1', type: 'Appraisal Fee', amount: 750, description: 'Independent appraiser fee' },
      { id: '2', type: 'Umpire Fee', amount: 1200, description: 'Umpire decision fee' },
      { id: '3', type: 'Engineer Fee', amount: 2500, description: 'Structural engineer report' },
    ],
    
    // PA Configuration
    paConfig: {
      percentage: 10, // PA fee percentage
      hireDate: '2024-01-01',
    },
  };
  
  // Proper insurance calculations
  const calculateTotalRCV = () => {
    return Object.values(sampleData.rcvBreakdown).reduce((sum, amount) => sum + amount, 0);
  };
  
  const calculateTotalACV = () => {
    return Object.values(sampleData.acvBreakdown).reduce((sum, amount) => sum + amount, 0);
  };
  
  const calculateTotalPriorPayments = () => {
    return sampleData.priorPayments.reduce((sum, payment) => sum + payment.amount, 0);
  };
  
  const calculateTotalUndisputedPayments = () => {
    return sampleData.undisputedPayments.reduce((sum, payment) => sum + payment.amount, 0);
  };
  
  const calculateTotalSettlementPayments = () => {
    return sampleData.settlementPayments.reduce((sum, payment) => sum + payment.amount, 0);
  };
  
  const calculateTotalPostLegalSettlements = () => {
    return sampleData.postLegalSettlements.reduce((sum, payment) => sum + payment.amount, 0);
  };
  
  const calculateTotalLegalFees = () => {
    return sampleData.postLegalSettlements.reduce((sum, payment) => sum + (payment.legalFees || 0), 0);
  };
  
  const calculateTotalClientExpenses = () => {
    return sampleData.clientExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  };
  
  const calculatePostHireTotal = () => {
    return calculateTotalUndisputedPayments() + calculateTotalSettlementPayments() + calculateTotalPostLegalSettlements();
  };
  
  const calculateGrossSettlement = () => {
    return calculateTotalPriorPayments() + calculatePostHireTotal();
  };
  
  const calculatePAFees = () => {
    const priorPayments = calculateTotalPriorPayments();
    const postHirePayments = calculatePostHireTotal();
    const deductible = sampleData.deductibles.applied;
    const paPercentage = sampleData.paConfig.percentage / 100;
    
    // If no prior payments: PA fees on (settlement - deductible) if > deductible
    if (priorPayments === 0) {
      const totalSettlement = postHirePayments;
      if (totalSettlement > deductible) {
        return (totalSettlement - deductible) * paPercentage;
      } else {
        return 0; // Insured gets nothing, no PA fees
      }
    }
    
    // If prior payments exist above deductible: PA fees on additional payments
    // (deductible already accounted for in prior payments)
    if (priorPayments > deductible) {
      return postHirePayments * paPercentage;
    }
    
    // If prior payments < deductible: PA fees on all payments beyond deductible
    const totalAboveDeductible = Math.max(0, (priorPayments + postHirePayments) - deductible);
    return totalAboveDeductible * paPercentage;
  };
  
  const calculateNetSettlement = () => {
    return calculateGrossSettlement() - calculatePAFees() - calculateTotalClientExpenses() - calculateTotalLegalFees();
  };
  
  const calculatePostLegalPAFees = () => {
    // PA receives post-legal fees for additional payments secured after legal referral
    const postLegalAmount = calculateTotalPostLegalSettlements();
    return postLegalAmount * (sampleData.paConfig.percentage / 100);
  };
  
  const calculateCoverageUtilization = (coverage: keyof typeof sampleData.coverageLimits) => {
    const limit = sampleData.coverageLimits[coverage];
    const rcv = sampleData.rcvBreakdown[coverage];
    if (limit === 0) return 0;
    return (rcv / limit) * 100;
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: '1400px', 
      margin: '0 auto', 
      background: 'white', 
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header - Purple Gradient */}
      <Box sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '24px',
        textAlign: 'center'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          💰 Payments Management
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Comprehensive payment tracking and financial overview
        </Typography>
      </Box>

      <Box sx={{ padding: '32px' }}>
        {/* Coverage Analysis Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            mb: 3,
            pb: '12px',
            borderBottom: '2px solid #e0e0e0'
          }}>
            <Box sx={{
              width: '28px',
              height: '28px', 
              background: '#667eea',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px'
            }}>
              🏠
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
              Coverage Analysis
            </Typography>
          </Box>
          
          {/* Coverage Tables Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '24px',
            mb: 4
          }}>
            {/* Coverage Limits Table */}
            <Card sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                🏛️ Policy Limits
              </Typography>
              <Box sx={{ space: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage A (Dwelling):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    ${sampleData.coverageLimits.coverageA.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage B (Other Property):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    ${sampleData.coverageLimits.coverageB.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage C (Personal Items):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    ${sampleData.coverageLimits.coverageC.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage D (ALE):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    ${sampleData.coverageLimits.coverageD.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Card>
            
            {/* RCV Table */}
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)', border: '2px solid #4CAF50', height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#2e7d32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                💰 RCV (Replacement Cost)
              </Typography>
              <Box sx={{ space: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage A:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                    ${sampleData.rcvBreakdown.coverageA.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage B:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                    ${sampleData.rcvBreakdown.coverageB.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage C:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                    ${sampleData.rcvBreakdown.coverageC.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage D (ALE):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                    ${sampleData.rcvBreakdown.coverageD.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ borderTop: '2px solid #4CAF50', pt: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Total RCV:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '1.1rem' }}>
                      ${calculateTotalRCV().toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
            
            {/* ACV Table */}
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)', border: '2px solid #FF9800', height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#f57c00', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 ACV (Actual Cash Value)
              </Typography>
              <Box sx={{ space: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage A:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    ${sampleData.acvBreakdown.coverageA.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage B:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    ${sampleData.acvBreakdown.coverageB.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage C:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    ${sampleData.acvBreakdown.coverageC.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Coverage D (No Depreciation):</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    ${sampleData.acvBreakdown.coverageD.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ borderTop: '2px solid #FF9800', pt: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Total ACV:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#e65100', fontSize: '1.1rem' }}>
                      ${calculateTotalACV().toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>
        
        {/* Financial Summary Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            mb: 3,
            pb: '12px',
            borderBottom: '2px solid #e0e0e0'
          }}>
            <Box sx={{
              width: '28px',
              height: '28px', 
              background: '#667eea',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px'
            }}>
              💰
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
              Financial Summary
            </Typography>
          </Box>

          {/* Summary Cards Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            mb: 4,
            width: '98%'
          }}>
            {/* Gross Settlement */}
            <Card sx={{ 
              textAlign: 'center', 
              p: 3,
              background: 'linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%)',
              border: '2px solid #4caf50',
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <Box sx={{ 
                width: '48px', 
                height: '48px', 
                margin: '0 auto 16px', 
                background: '#667eea', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '20px' 
              }}>
                💰
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                {formatCurrency(calculateGrossSettlement())}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#666', 
                fontWeight: 500, 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                mb: 1
              }}>
                Gross Settlement
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                Prior + Undisputed + Settlement + Post-Legal
              </Typography>
            </Card>

            {/* PA Fees */}
            <Card sx={{ 
              textAlign: 'center', 
              p: 3,
              background: 'linear-gradient(135deg, #f0f8ff 0%, #e3f2fd 100%)',
              border: '2px solid #2196f3',
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <Box sx={{ 
                width: '48px', 
                height: '48px', 
                margin: '0 auto 16px', 
                background: '#667eea', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '20px' 
              }}>
                🏛️
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                {formatCurrency(calculatePAFees())}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#666', 
                fontWeight: 500, 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                mb: 1
              }}>
                PA Fees
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                {sampleData.paConfig.percentage}% on settlement above deductible
              </Typography>
            </Card>

            {/* Client Expenses */}
            <Card sx={{ 
              textAlign: 'center', 
              p: 3,
              background: 'linear-gradient(135deg, #fff8f0 0%, #ffeaa7 100%)',
              border: '2px solid #ff9800',
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <Box sx={{ 
                width: '48px', 
                height: '48px', 
                margin: '0 auto 16px', 
                background: '#667eea', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '20px' 
              }}>
                📋
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                {formatCurrency(calculateTotalClientExpenses())}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#666', 
                fontWeight: 500, 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                mb: 1
              }}>
                Client Expenses
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                Appraisal, umpire, and engineering fees
              </Typography>
            </Card>

            {/* Legal Fees */}
            <Card sx={{ 
              textAlign: 'center', 
              p: 3,
              background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
              border: '2px solid #e91e63',
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <Box sx={{ 
                width: '48px', 
                height: '48px', 
                margin: '0 auto 16px', 
                background: '#667eea', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '20px' 
              }}>
                ⚖️
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                {formatCurrency(calculateTotalLegalFees())}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#666', 
                fontWeight: 500, 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                mb: 1
              }}>
                Legal Fees
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                Law firm fees for legal referrals
              </Typography>
            </Card>

            {/* Net Settlement */}
            <Card sx={{ 
              textAlign: 'center', 
              p: 3,
              background: 'linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%)',
              border: '2px solid #4caf50',
              borderRadius: '12px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <Box sx={{ 
                width: '48px', 
                height: '48px', 
                margin: '0 auto 16px', 
                background: '#667eea', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '20px' 
              }}>
                💵
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 1 }}>
                {formatCurrency(calculateNetSettlement())}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#666', 
                fontWeight: 500, 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                mb: 1
              }}>
                Net Settlement
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                Final amount to client after all deductions
              </Typography>
            </Card>

          </Box>
        </Box>
        
        {/* Payment Categories Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            mb: 3,
            pb: '12px',
            borderBottom: '2px solid #e0e0e0'
          }}>
            <Box sx={{
              width: '28px',
              height: '28px', 
              background: '#667eea',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px'
            }}>
              💳
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
              Payment Categories
            </Typography>
          </Box>
          
          {/* Payment Categories Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            mb: 4
          }}>
            {/* Prior Payments */}
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '2px solid #2196F3' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                🏦 Prior Payments
              </Typography>
              <Box sx={{ space: 1 }}>
                {sampleData.priorPayments.map((payment, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{payment.type} - {payment.coverage}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>
                      ${payment.amount.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ borderTop: '2px solid #2196F3', pt: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Total Prior:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1565c0', fontSize: '1.1rem' }}>
                      ${calculateTotalPriorPayments().toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', mt: 1, display: 'block' }}>
                Standard don't go to PA at all
              </Typography>
            </Card>
            
            {/* Undisputed Payments */}
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', border: '2px solid #4CAF50' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#2e7d32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                ✅ Undisputed Payments
              </Typography>
              <Box sx={{ space: 1 }}>
                {sampleData.undisputedPayments.map((payment, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{payment.type} - {payment.coverage}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                      ${payment.amount.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ borderTop: '2px solid #4CAF50', pt: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Total Undisputed:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '1.1rem' }}>
                      ${calculateTotalUndisputedPayments().toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', mt: 1, display: 'block' }}>
                Insurance pays without binding final settlement
              </Typography>
            </Card>
            
            {/* Settlement Payments */}
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)', border: '2px solid #FF9800' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#f57c00', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                🤝 Settlement Payments
              </Typography>
              <Box sx={{ space: 1 }}>
                {sampleData.settlementPayments.map((payment, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{payment.type} - {payment.coverage}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                      ${payment.amount.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ borderTop: '2px solid #FF9800', pt: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Total Settlement:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#e65100', fontSize: '1.1rem' }}>
                      ${calculateTotalSettlementPayments().toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', mt: 1, display: 'block' }}>
                Negotiated through litigation/mediation/arbitration/appraisal
              </Typography>
            </Card>
          </Box>
        </Box>
        
        {/* Deductibles and Fees Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            mb: 3,
            pb: '12px',
            borderBottom: '2px solid #e0e0e0'
          }}>
            <Box sx={{
              width: '28px',
              height: '28px', 
              background: '#667eea',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px'
            }}>
              📋
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
              Deductibles & Fees
            </Typography>
          </Box>
          
          {/* Deductibles and Fees Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            mb: 4
          }}>
            {/* Deductibles */}
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #fff8f0 0%, #ffeaa7 100%)', border: '2px solid #FF9800' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#f57c00', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                ⚠️ Deductibles
              </Typography>
              <Box sx={{ space: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Wind/Hail:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    ${sampleData.deductibles.windHail.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>All Other Perils:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                    ${sampleData.deductibles.allOtherPerils.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ borderTop: '2px solid #FF9800', pt: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Applied Deductible:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#e65100', fontSize: '1.1rem' }}>
                      ${sampleData.deductibles.applied.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
            
            {/* Client Expenses */}
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', border: '2px solid #9C27B0' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#7b1fa2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                📋 Client Expenses
              </Typography>
              <Box sx={{ space: 1 }}>
                {sampleData.clientExpenses.map((expense, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{expense.description}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#6a1b9a' }}>
                      ${expense.amount.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ borderTop: '2px solid #9C27B0', pt: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>Total Expenses:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#6a1b9a', fontSize: '1.1rem' }}>
                      ${calculateTotalClientExpenses().toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', mt: 1, display: 'block' }}>
                Deducted from settlement
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Payment Sections */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            mb: 3,
            pb: '12px',
            borderBottom: '2px solid #e0e0e0'
          }}>
            <Box sx={{
              width: '28px',
              height: '28px', 
              background: '#667eea',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px'
            }}>
              📋
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
              Payment Details
            </Typography>
          </Box>

          {/* Payments Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '24px'
          }}>
            {/* Prior Payments */}
            <Card sx={{
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '12px',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <Box 
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}
                onClick={() => toggleSection('priorPayments')}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Prior Payments
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {sampleData.priorPayments.length} payments
                  </Box>
                  <IconButton size="small" sx={{ color: 'white' }}>
                    {expandedSections.priorPayments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>
              <Collapse in={expandedSections.priorPayments}>
                <Box sx={{ p: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Payer</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sampleData.priorPayments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                            <TableCell>{payment.payer}</TableCell>
                            <TableCell>{payment.type}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                              {formatCurrency(payment.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Collapse>
            </Card>

            {/* Final Settlement Payments */}
            <Card sx={{
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '12px',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <Box 
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}
                onClick={() => toggleSection('settlementPayments')}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Final Settlement Payments
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 500
                  }}>
                    {sampleData.settlementPayments.length} payment
                  </Box>
                  <IconButton size="small" sx={{ color: 'white' }}>
                    {expandedSections.settlementPayments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>
              <Collapse in={expandedSections.settlementPayments}>
                <Box sx={{ p: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sampleData.settlementPayments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                            <TableCell>{payment.type}</TableCell>
                            <TableCell>{payment.method}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                              {formatCurrency(payment.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Collapse>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentsTab;
